'use client';

import { useSelector } from '@xstate/store/react';
import Image from 'next/image';
import Link from 'next/link';

import IconHeartActive from '@/fo/icons/base/icons/heart-active.svg';
import IconHeart from '@/fo/icons/base/icons/heart.svg';
import { storeProduct, selectWishlistItemId } from '@/fo/store/product';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiProductOptionsBase } from '@/fo/ui/product/options/base';
import { MappedProduct } from '@/fo/util/map-product-data';

import { UiProductTileSecondaryProps } from '../../lib/UiProductTileSecondary';

import { styles } from './ProductInGroupTile.styles';

export interface ProductInGroupTileProps {
  data: MappedProduct;
  type: UiProductTileSecondaryProps['type'];
  onWishlistClick?: (
    sku: string,
    wishlistSku: string,
    parentSku?: string,
    wishlistItemId?: string,
    isInWishlist?: boolean
  ) => void;
  childrenLink?: (sku: string) => string;
}

export function ProductInGroupTile({
  data,
  type,
  onWishlistClick
}: ProductInGroupTileProps) {
  const slots = styles();
  const { sku, name, basePrice, configurableVariants, thumbnail, link } = data;

  const wishlistItemId = useSelector(storeProduct, selectWishlistItemId(sku));
  const isInWishlist = Boolean(wishlistItemId);
  const wishlistSku = data.parentSku || data?.sku;
  const parentSku = data.parentSku;

  const handleWishlistClick = () => {
    onWishlistClick?.(
      sku,
      wishlistSku,
      parentSku,
      wishlistItemId,
      isInWishlist
    );
  };

  return (
    <div
      key={sku}
      className={slots.base()}
    >
      {link && (basePrice ?? 0) > 0 && (
        <Link
          className={slots.link()}
          href={link}
        />
      )}
      <div className={slots.imageContainer()}>
        <Image
          className={slots.image()}
          src={thumbnail?.src || ''}
          alt={name}
          width={60}
          height={60}
        />
      </div>
      <div className={slots.content()}>
        <h2
          className={slots.title()}
          dangerouslySetInnerHTML={{ __html: name }}
        />
        <div className={slots.priceRow()}>
          {configurableVariants && configurableVariants.length > 0 && (
            <UiProductOptionsBase
              options={configurableVariants}
              size="s"
              maxVisible={3}
              noSelected={true}
            />
          )}
          <UiKitPriceBase
            value={basePrice || 0}
            classNameValue="heading-s-bold"
          />
        </div>
      </div>
      <div className={slots.actions()}>
        {type === 'base' && (
          <button
            type="button"
            className={slots.actionItem({ isInWishlist })}
            onClick={handleWishlistClick}
            data-mobile-app-hidden="commerce"
          >
            {isInWishlist ? (
              <IconHeartActive className="size-6" />
            ) : (
              <IconHeart className="size-6" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
